import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  Lightbulb, Rocket, FlaskConical, CheckCircle2, ArrowRight, Sparkles,
  Search, Hammer, PackageCheck, Layers, Code2, Puzzle, Server
} from 'lucide-react';

// Roadmap of future / in-progress extension ideas.
const ROADMAP = [
  {
    stage: 'Researching',
    icon: Search,
    accent: 'from-amber-500 to-orange-500',
    badgeCls: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    items: [
      { name: 'AI Content Assistant', type: 'Plugin', desc: 'Generate & rewrite Joomla article content inline with AI, with tone and SEO controls.' },
      { name: 'Smart Related Content', type: 'Module', desc: 'Vector-based related articles and products that actually match context.' },
      { name: 'Accessibility Auditor', type: 'Component', desc: 'Scan your site for WCAG issues and get one-click fixes.' },
    ],
  },
  {
    stage: 'Planned',
    icon: Lightbulb,
    accent: 'from-blue-500 to-cyan-500',
    badgeCls: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    items: [
      { name: 'Advanced SEF & Redirect Manager', type: 'Component', desc: 'Clean URLs, bulk redirects, and 404 monitoring in one dashboard.' },
      { name: 'GDPR Cookie Consent', type: 'Plugin', desc: 'Configurable consent banner with granular script blocking.' },
      { name: 'Headless API Bridge', type: 'Component', desc: 'Expose Joomla content through a secure REST/GraphQL layer.' },
    ],
  },
  {
    stage: 'In Development',
    icon: Hammer,
    accent: 'from-purple-500 to-indigo-500',
    badgeCls: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
    items: [
      { name: 'Membership & Subscriptions', type: 'Component', desc: 'Recurring access control with Stripe billing and drip content.' },
      { name: 'Booking & Appointments', type: 'Component', desc: 'Calendar-based scheduling with reminders and staff routing.' },
    ],
  },
  {
    stage: 'Shipped',
    icon: PackageCheck,
    accent: 'from-emerald-500 to-teal-500',
    badgeCls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    items: [
      { name: 'License & Auto-Update Server', type: 'Component', desc: 'Deliver gated downloads and update feeds to licensed Joomla sites.' },
    ],
  },
];

const TYPE_ICON = { Component: Layers, Module: Puzzle, Plugin: Code2, Template: Server };

const RoadmapPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-bots-dark" data-testid="roadmap-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-16">
        <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] bg-blue-600/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[36rem] h-[36rem] bg-purple-600/20 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-sm font-semibold mb-6">
            <Rocket className="w-4 h-4" /> Product Roadmap
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-6">
            What we're{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">building next</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-8">
            A living look at the Joomla extensions our team is researching, planning and
            developing — all built for Joomla 4, 5 &amp; 6. Have an idea we should add?
          </p>
          <Link
            to="/suggest-extension"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-500 text-white font-bold rounded-full hover:from-purple-500 hover:to-indigo-400 transition-all transform hover:scale-105 shadow-lg shadow-purple-500/30"
            data-testid="roadmap-suggest-cta"
          >
            <Lightbulb className="w-5 h-5" /> Suggest an Extension
          </Link>
        </div>
      </section>

      {/* Roadmap columns */}
      <section className="py-12 bg-bots-surface border-y border-blue-500/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ROADMAP.map((col) => {
              const StageIcon = col.icon;
              return (
                <div key={col.stage} className="flex flex-col" data-testid={`roadmap-col-${col.stage.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${col.accent} flex items-center justify-center shadow-lg`}>
                      <StageIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold">{col.stage}</h3>
                      <span className="text-xs text-gray-400">{col.items.length} idea{col.items.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <div className="space-y-4 flex-1">
                    {col.items.map((item) => {
                      const TypeIcon = TYPE_ICON[item.type] || Puzzle;
                      return (
                        <div key={item.name} className="bg-bots-dark/60 border border-white/10 rounded-xl p-4 hover:border-blue-400/40 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${col.badgeCls}`}>
                              {col.stage}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                              <TypeIcon className="w-3.5 h-3.5" /> {item.type}
                            </span>
                          </div>
                          <h4 className="text-white font-semibold text-sm mb-1">{item.name}</h4>
                          <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-bots-dark to-bots-surface">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <FlaskConical className="w-12 h-12 text-purple-400 mx-auto mb-5" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Missing something you need?</h2>
          <p className="text-gray-300 text-lg mb-8">
            Our roadmap is shaped by the Joomla community. Tell us what you'd love to see —
            popular suggestions get prioritized and built.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/suggest-extension"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold rounded-full hover:from-blue-500 hover:to-cyan-400 transition-all"
              data-testid="roadmap-cta-suggest"
            >
              <Sparkles className="w-5 h-5" /> Suggest an Extension
            </Link>
            <Link
              to="/custom-extensions"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/5 border border-white/15 text-white font-bold rounded-full hover:bg-white/10 transition-all"
              data-testid="roadmap-cta-custom"
            >
              Need it now? Request a Custom Build <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RoadmapPage;
